import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const InvoicePage = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    // Extract data from URL
    const ref = searchParams.get('ref') || 'N/A';
    const name = searchParams.get('name') || 'Guest';
    const email = searchParams.get('email') || 'N/A';
    const phone = searchParams.get('phone') || 'N/A';
    const checkin = searchParams.get('checkin') || 'N/A';
    const checkout = searchParams.get('checkout') || 'N/A';
    const room = searchParams.get('room') || 'N/A';
    const guests = searchParams.get('guests') || 'N/A';
    const total = searchParams.get('total') || '0';
    const download = searchParams.get('download') === 'true';

    const handleDownload = async () => {
        const element = document.getElementById('invoice-content');
        if (!element) return;

        try {
            const canvas = await html2canvas(element, { scale: 2 });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Invoice-${ref}.pdf`);
        } catch (error) {
            console.error('PDF Generation Error:', error);
        }
    };

    useEffect(() => {
        if (download) {
            // Small delay to ensure content is rendered before capturing
            const timer = setTimeout(() => {
                handleDownload();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [download]);

    return (
        <div className="min-h-screen bg-muted flex flex-col items-center py-12 px-4">
            <div className="mb-8 flex space-x-4 print:hidden">
                <Button onClick={handleDownload} variant="default" iconName="Download">
                    Download PDF Invoice
                </Button>
                <Button onClick={() => window.print()} variant="outline" iconName="Printer">
                    Print Invoice
                </Button>
            </div>

            <div
                id="invoice-content"
                className="w-full max-w-[800px] bg-white text-black p-12 shadow-2xl rounded-sm font-sans"
            >
                {/* Header */}
                <div className="flex justify-between items-start border-b-2 border-primary pb-8 mb-8">
                    <div>
                        <h1 className="text-4xl font-bold text-primary mb-2">INVOICE</h1>
                        <p className="text-lg font-semibold">Dandeli Adventure Resorts</p>
                        <p className="text-sm text-gray-600">Dandeli, Karnataka, India</p>
                        <p className="text-sm text-gray-600">Contact: +91 98765 43210</p>
                    </div>
                    <div className="text-right">
                        <p className="text-sm font-bold text-gray-500">Booking Reference</p>
                        <p className="text-xl font-mono text-primary font-bold">#{ref}</p>
                        <p className="text-sm mt-4">Date: {new Date().toLocaleDateString('en-IN')}</p>
                    </div>
                </div>

                {/* Client Info */}
                <div className="grid grid-cols-2 gap-8 mb-12">
                    <div>
                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-2">Billed To</h3>
                        <p className="text-lg font-bold">{name}</p>
                        <p className="text-sm">{email}</p>
                        <p className="text-sm">{phone}</p>
                    </div>
                    <div className="text-right">
                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-2">Stay Details</h3>
                        <p className="text-sm"><strong>Accommodation:</strong> {room}</p>
                        <p className="text-sm"><strong>Check-in:</strong> {checkin}</p>
                        <p className="text-sm"><strong>Check-out:</strong> {checkout}</p>
                        <p className="text-sm"><strong>Guests:</strong> {guests}</p>
                    </div>
                </div>

                {/* Table */}
                <table className="w-full mb-12">
                    <thead>
                        <tr className="bg-primary text-white">
                            <th className="py-3 px-4 text-left">Description</th>
                            <th className="py-3 px-4 text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b">
                            <td className="py-4 px-4">
                                <p className="font-bold">{room} - Accommodation Charge</p>
                                <p className="text-xs text-gray-500">Package includes stay and breakfast</p>
                            </td>
                            <td className="py-4 px-4 text-right">Included in Total</td>
                        </tr>
                    </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end">
                    <div className="w-64">
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-500">Subtotal</span>
                            <span className="font-bold">₹{total}</span>
                        </div>
                        <div className="flex justify-between mb-4">
                            <span className="text-gray-500">Tax (Included)</span>
                            <span className="font-bold">₹0</span>
                        </div>
                        <div className="flex justify-between border-t-2 border-primary pt-4">
                            <span className="text-xl font-bold">TOTAL</span>
                            <span className="text-xl font-bold text-primary">₹{total}</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-24 pt-8 border-t border-gray-200 text-center text-xs text-gray-400">
                    <p className="mb-2">Thank you for choosing Dandeli Adventure Resorts!</p>
                    <p>Please present this invoice at the time of check-in.</p>
                    <p className="mt-4">This is a system generated invoice.</p>
                </div>
            </div>
        </div>
    );
};

export default InvoicePage;
